import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Table,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { makeStyles } from '@mui/styles';
import { formatDate } from "utils";

import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BootstrapDialogTitle from "components/CustomDialogTitle";
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const useStyles = makeStyles((theme) => ({
  violationBox: {
    width: '100%',
    height: '200px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.spacing(2),
    flexDirection: 'column',
    gap: theme.spacing(6),
    padding: theme.spacing(6)
  },
  proctorGreen: {
    backgroundColor: theme.palette.success['clr-100'],
  },
  proctorRed: {
    backgroundColor: theme.palette.danger['clr-100'],
  },
  timeStamp: {
    width: '252px',
    height: '232px',
    borderRadius: theme.spacing(2),
    border: `2px solid ${theme.palette.neutral['clr-400']}`
  },
  counts: {
    display: 'flex',
    gap: 8,

  },
  count: {
    display: 'flex',
    border: `1px solid ${theme.palette.neutral['clr-200']}`,
    padding: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    borderRadius: 8,
    width: 180,
    textAlign: 'center'
  }
}))

const Tips = [
  [
    "Sit upright with your back well-supported.",
    "Align your webcam to maintain direct eye contact.",
    "Relax your shoulders and keep your hands calm on the desk.",
    "Ensure your screen is at a comfortable viewing distance.",
  ],
  [
    "Sit tall with your back firmly supported.",
    "Adjust your webcam so you’re looking straight into it.",
    "Keep shoulders loose and hands resting gently.",
    "Position your screen at an easy-to-view distance.",
  ],
  [
    "Maintain an upright posture with your back against the chair.",
    "Place the webcam at eye level for natural interaction.",
    "Let your shoulders relax and hands stay steady.",
    "Ensure the screen is neither too close nor too far.",
  ]
];

const Counts = {
  "MOVE_AWAY": { count: 0, label: 'Moved away from screen' },
  "TAB_SWITCH": { count: 0, label: 'Switched browser tabs' },
  "EXIT_FULL_SCREEN": { count: 0, label: 'Exited full-screen mode' },
};

export default function ProctorshipDialog({ open, onClose, violated, records }) {
  const classes = useStyles();
  const [tabIndex, setTabIndex] = useState(0);
  const [snapshots, setSnapshots] = useState([]);
  const [violations, setViolations] = useState([]);
  const [counts, setCounts] = useState(Counts);

  const handleTabChange = (_event, newValue) => {
    setTabIndex(newValue);
  };

  React.useEffect(() => {
    const _violations = [], _snapshots = [];
    const _counts = { ...counts };

    records.forEach((record) => {
      switch (record.type) {
        case 'IMAGE_PROCTOR':
          _snapshots.push({ ...record });
          break;
        case 'MOVE_AWAY':
          _violations.push({ ...record, name: 'Moved away from screen' });
          _counts.MOVE_AWAY.count = _counts.MOVE_AWAY.count + 1;
          break;
        case 'TAB_SWITCH':
          _violations.push({ ...record, name: 'Switched browser tabs' });
          _counts.TAB_SWITCH.count = _counts.TAB_SWITCH.count + 1;
          break;
        case 'CAMERA_DISABLED':
          _violations.push({ ...record, name: 'Camera was disabled during the assessment' });
          break;
        case 'EXIT_FULL_SCREEN':
          _violations.push({ ...record, name: 'Exited full-screen mode' });
          _counts.EXIT_FULL_SCREEN.count = _counts.EXIT_FULL_SCREEN.count + 1;
          break;

        default:
          break;
      }
    });

    setViolations(_violations);
    setSnapshots(_snapshots);
    setCounts(_counts);
  }, [records]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <BootstrapDialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: '16px' }}
      >
        <Box display="flex" alignItems="center">
          <Typography variant="h5-medium">Proctorship</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ padding: 0 }}>
          <CloseIcon />
        </IconButton>
      </BootstrapDialogTitle>

      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="fullWidth"
        TabIndicatorProps={{ style: { display: "none" } }}
      >
        {["Interview violations", "Posture alignment", "Snapshots"].map((label, index) => (
          <Tab
            key={label}
            label={label}
            sx={{
              flex: 1,
              borderBottom: 3,
              borderColor: tabIndex === index ? "primary.main" : "neutral.clr-200",
              color: "text.primary",
              textTransform: "none",
              fontWeight: 500,
              fontSize: '18px'
            }}
          />
        ))}
      </Tabs>
      <DialogContent
        sx={{ padding: '16px', maxHeight: '482px', height: '482px', overflow: 'auto' }}
      >

        {tabIndex === 0 && (
          <Box display='flex' flexDirection='column' gap='20px'>
            <Box
              className={`${classes.violationBox} ${violated ? classes.proctorRed : classes.proctorGreen}`}
            >
              {violated
                ? <ErrorOutlineIcon sx={{ fontSize: 60, color: "#C51407" }} />
                : <CheckIcon sx={{ fontSize: 60, color: "green", border: `4px solid green`, borderRadius: '50%' }} />}
              {violated
                ? (
                  <Typography variant="h6-medium" color='danger.clr-700' textAlign='center'>
                    Violation found! Don’t do it even by mistake, as in real interviews,
                    the AI proctoring system will reject you.
                  </Typography>
                )
                : (
                  <Typography variant="h6-medium" textAlign='center'>
                    Great job! No violations occurred.
                  </Typography>
                )}
            </Box>

            {violated && (
              <>
                <Box className={classes.counts}>

                  {
                    Object.values(counts).map(({ count, label }) => (
                      <Box className={classes.count}>
                        <Typography variant="body01-medium" color='neutral.clr-900'>
                          {label}
                        </Typography>
                        <Typography
                          variant="h5-bold"
                          color={count > 0 ? 'danger.clr-700' : 'success.clr-700'}>
                          {count}
                        </Typography>
                      </Box>
                    ))
                  }

                </Box>

                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #E0E3E7",
                    maxWidth: 600,
                    mx: "auto",
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#F7FAFC" }}>
                        <TableCell sx={{ fontWeight: "bold" }}>Violation</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Timestamp</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {violations.map((row, idx) => (
                        <TableRow
                          key={idx}
                          sx={{
                            "&:not(:last-child) td": { borderBottom: "1px solid #E0E3E7" },
                          }}
                        >
                          <TableCell>{row?.name}</TableCell>
                          <TableCell>{formatDate(row?.timestamp, 'DD-MM-YYYY hh:mm A')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

          </Box>
        )}


        {tabIndex === 1 && (
          <Box display='flex' flexDirection='column' gap='20px' alignItems='center'>
            <img
              src="https://assets.languify.in/images/proctorship.png"
              alt="violation"
              width={392}
            />
            <Box display='flex' flexDirection='column' gap='12px'>
              <Typography variant="body01-bold" color='success.clr-700'>
                Remember these tips for better body posture while sitting for the interview
              </Typography>
              <List disablePadding>
                {Tips[Math.floor(Math.random() * 3)].map((tip, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                          {`• ${tip}`}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        )}


        {tabIndex === 2 && (
          <Box display='flex' flexWrap='wrap' gap='16px' justifyContent='left'>
            {
              snapshots.map((snapshot, i) => (
                <Box className={classes.timeStamp} key={i} >

                  <Box width={'100%'} height={'80%'}>
                    <img
                      src={snapshot?.location}
                      alt="snapshot"
                      width={'100%'}
                    />
                  </Box>

                  <Box display='flex' justifyContent='center' alignItems='center' mt={2}>
                    <AccessTimeIcon fontSize="small" color="#3C434D" />
                    <Typography color='#3C434D'>
                      {formatDate(snapshot?.timestamp, 'DD-MM-YYYY hh:mm A')}
                    </Typography>
                  </Box>
                </Box>
              ))
            }

            {
              !snapshots.length && (
                <Box display='flex' textAlign='center' mt={4}>
                  <Typography variant="body01-bold" color='neutral.clr-600'>
                    No snapshots taken during the interview.
                  </Typography>
                </Box>
              )
            }

          </Box>
        )}

      </DialogContent>
    </Dialog>
  );
}
